<template>
  <Dialog
    :open="open"
    @update:open="handleClose"
  >
    <DialogContent class="w-[609px] p-6">
      <div data-cy="instructions-dialog">
        <DialogHeader>
          <DialogTitle class="text-xl font-semibold text-gray-800">
            {{ t('addInstructions') }}
          </DialogTitle>
        </DialogHeader>

        <!-- Informational Banner -->
        <InformationalBanner :icon="Sparkles">
          {{ t('instructionsInfo') }}
        </InformationalBanner>

        <!-- Instructions Textarea -->
        <div class="space-y-1">
          <label class="text-sm font-medium text-gray-600">
            {{ t('instructions') }}
          </label>
          <div class="relative">
            <textarea
              v-model="form.instructions"
              :placeholder="t('instructionsPlaceholder')"
              :maxlength="maxCharacters"
              rows="4"
              data-cy="instructions-textarea"
              class="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-base shadow-sm
                placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <div class="absolute bottom-2 right-2 text-xs text-gray-500">
              {{ characterCount }}/{{ maxCharacters }} {{ t('characters') }}
            </div>
          </div>
          <div
            v-if="errors.instructions"
            class="text-sm text-red-600"
          >
            {{ errors.instructions }}
          </div>
        </div>

        <!-- General Error -->
        <div
          v-if="errors.general"
          class="text-sm text-red-600"
        >
          {{ errors.general }}
        </div>
      </div>

      <DialogFooter class="gap-2">
        <Button
          variant="secondary"
          :disabled="isSubmitting"
          data-cy="cancel-instructions-button"
          @click="handleClose"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          :disabled="isSubmitting"
          class="bg-purple-600 hover:bg-purple-700"
          data-cy="save-instructions-button"
          @click="handleSubmit"
        >
          {{ submitText }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
  import { Button } from '@/components/ui/button'
  import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import InformationalBanner from '@/components/ui/informational-banner/InformationalBanner.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import type { SessionGroup } from '@/types/generated'
  import { Sparkles } from 'lucide-vue-next'
  import { computed, ref, watch } from 'vue'

  interface Props {
    open: boolean
    folder?: SessionGroup | null
    initialInstructions?: string
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    close: []
    save: [instructions: string, folder?: SessionGroup | null]
  }>()

  const { t } = useTranslation({
    en: {
      addInstructions: 'Add Instructions',
      instructionsInfo:
        'You can ask AI to focus on certain topics, or ask it to use a certain tone or format for responses.',
      instructions: 'Instructions',
      instructionsPlaceholder: 'Enter instructions for AI responses in this folder...',
      cancel: 'Cancel',
      save: 'Save Instructions',
      saving: 'Saving...',
      characters: 'characters'
    },
    de: {
      addInstructions: 'Anweisungen hinzufügen',
      instructionsInfo:
        'Sie können die KI bitten, sich auf bestimmte Themen zu konzentrieren oder einen bestimmten Ton oder ein bestimmtes Format für Antworten zu verwenden.',
      instructions: 'Anweisungen',
      instructionsPlaceholder: 'Geben Sie Anweisungen für KI-Antworten in diesem Ordner ein...',
      cancel: 'Abbrechen',
      save: 'Anweisungen speichern',
      saving: 'Speichern...',
      characters: 'Zeichen'
    }
  })

  const maxCharacters = 1000
  const isSubmitting = ref(false)
  const errors = ref<{
    instructions?: string
    general?: string
  }>({})

  const form = ref({
    instructions: ''
  })

  const characterCount = computed(() => form.value.instructions.length)

  const submitText = computed(() => {
    return isSubmitting.value ? t('saving') : t('save')
  })

  // Watch for prop changes
  watch(
    () => props.open,
    (isOpen) => {
      if (isOpen) {
        form.value.instructions = props.initialInstructions || ''
        errors.value = {}
      }
    }
  )

  watch(
    () => props.initialInstructions,
    (newInstructions) => {
      if (props.open) {
        form.value.instructions = newInstructions || ''
      }
    }
  )

  const validateForm = () => {
    errors.value = {}

    // Allow empty instructions (user might want to clear them)
    if (form.value.instructions.length > maxCharacters) {
      errors.value.instructions = `Instructions must be ${maxCharacters} characters or less`
      return false
    }

    return true
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    try {
      isSubmitting.value = true
      errors.value = {}

      emit('save', form.value.instructions.trim(), props.folder)
    } catch (error) {
      console.error('Error saving instructions:', error)
      errors.value.general = 'Failed to save instructions'
    } finally {
      isSubmitting.value = false
    }
  }

  const handleClose = () => {
    if (!isSubmitting.value) {
      emit('close')
    }
  }
</script>
